// Validation utilities using Zod

import { z } from 'zod';
import {
  CURRENCY_CODES,
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
  INVOICE_STATUSES,
  CUSTOMER_STATUSES,
  CUSTOMER_TYPES,
  EXPENSE_STATUSES,
  EXPENSE_FREQUENCIES,
  COMMUNICATION_CHANNELS,
} from '../constants/index';

// ── Shared Zod enum schemas (derived from constants) ──────────────────────
export const currencyCodeSchema = z.enum(CURRENCY_CODES);
export const paymentMethodEnumSchema = z.enum(PAYMENT_METHODS);
export const paymentStatusEnumSchema = z.enum(PAYMENT_STATUSES);
export const invoiceStatusEnumSchema = z.enum(INVOICE_STATUSES);
export const customerStatusEnumSchema = z.enum(CUSTOMER_STATUSES);
export const customerTypeEnumSchema = z.enum(CUSTOMER_TYPES);
export const expenseStatusEnumSchema = z.enum(EXPENSE_STATUSES);
export const expenseFrequencyEnumSchema = z.enum(EXPENSE_FREQUENCIES);
export const communicationChannelEnumSchema = z.enum(COMMUNICATION_CHANNELS);

// Common validation schemas
export const emailSchema = z.string().email('Invalid email address');

export const phoneSchema = z.string().regex(/^[+]?[1-9]\d{1,14}$/, 'Invalid phone number format');

export const urlSchema = z.string().url('Invalid URL format');

// MongoDB ObjectId validation
export const objectIdSchema = z.string().regex(/^[0-9a-fA-F]{24}$/, 'Invalid ObjectId format');

// Date validation
export const dateSchema = z.union([z.date(), z.string().transform(str => new Date(str))]);

// Currency validation
export const currencySchema = z
  .string()
  .regex(/^[A-Z]{3}$/, 'Currency must be a 3-letter ISO code');

// Tax ID validation for Indonesia
export const taxIdSchema = z
  .string()
  .regex(/^\d{2}\.\d{3}\.\d{3}\.\d{1}-\d{3}\.\d{3}$/, 'Invalid Indonesian Tax ID format (NPWP)')
  .or(z.string().regex(/^\d{15}$/, 'Invalid Indonesian Tax ID format (15 digits)'));

// Address validation schema
export const addressSchema = z.object({
  street: z.string().min(1, 'Street is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().optional(),
  postalCode: z.string().min(1, 'Postal code is required'),
  country: z.string().min(2, 'Country is required').max(2, 'Country must be 2-letter code'),
});

// Pagination validation
export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1, 'Page must be at least 1').default(1),
  limit: z.coerce.number().int().min(1).max(100, 'Limit cannot exceed 100').default(10),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional().default('asc'),
});

// Validation helper functions
export const validateEmail = (email: string): boolean => {
  return emailSchema.safeParse(email).success;
};

export const validatePhone = (phone: string): boolean => {
  return phoneSchema.safeParse(phone).success;
};

export const validateObjectId = (id: string): boolean => {
  return objectIdSchema.safeParse(id).success;
};

export const validateTaxId = (taxId: string): boolean => {
  return taxIdSchema.safeParse(taxId).success;
};

// Safe parsing helper that throws ValidationError
export const safeParse = <T>(schema: z.ZodSchema<T>, data: unknown): T => {
  const result = schema.safeParse(data);
  if (!result.success) {
    throw new Error(`Validation failed: ${result.error.message}`);
  }
  return result.data;
};

// Validation result type
export interface ValidationResult<T> {
  success: boolean;
  data?: T;
  errors?: z.ZodIssue[];
}

// Safe validation that returns result instead of throwing
export const validate = <T>(schema: z.ZodSchema<T>, data: unknown): ValidationResult<T> => {
  const result = schema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  } else {
    return { success: false, errors: result.error.errors };
  }
};
